import { httpClient } from './httpClient';

// Types for billing API
export interface CreateSubscriptionRequest {
  email: string;
}

export interface SubscriptionResponse {
  checkoutUrl: string;
  sessionId: string;
  userId: string;
  status: string;
}

export interface SubscriptionStatus {
  userId: string;
  status: string;
  planId?: string;
  planName?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodEnd?: string;
}

// Custom error class for billing API errors
export class BillingApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: any
  ) {
    super(message);
    this.name = 'BillingApiError';
  }
}

// Billing API service
export const billingApi = {
  /**
   * Create a Stripe checkout session for subscription
   */
  async createCheckoutSession(
    email: string,
    cohort?: string
  ): Promise<SubscriptionResponse> {
    try {
      const response = await httpClient.post<SubscriptionResponse>(
        '/billing/subscriptions',
        cohort ? { email, cohort } : { email }
      );

      return response;
    } catch (error: any) {
      if (error.code) {
        throw new BillingApiError(
          error.message || 'Failed to create checkout session',
          error.code,
          error.status || 400,
          error.details
        );
      }
      throw new BillingApiError(
        'Failed to create checkout session. Please try again.',
        'CHECKOUT_SESSION_FAILED',
        400
      );
    }
  },

  /**
   * Get current user's subscription
   */
  async getSubscription(): Promise<SubscriptionStatus> {
    try {
      const response = await httpClient.get<SubscriptionStatus>('/billing/subscriptions');
      return response;
    } catch (error: any) {
      if (error.status === 404) {
        throw new BillingApiError(
          'No subscription found',
          'SUBSCRIPTION_NOT_FOUND',
          404
        );
      }
      if (error.code) {
        throw new BillingApiError(
          error.message || 'Failed to get subscription',
          error.code,
          error.status || 400,
          error.details
        );
      }
      throw new BillingApiError(
        'Failed to get subscription',
        'GET_SUBSCRIPTION_FAILED',
        400
      );
    }
  },

  /**
   * Cancel current subscription
   */
  async cancelSubscription(): Promise<SubscriptionStatus> {
    try {
      const response = await httpClient.delete<SubscriptionStatus>('/billing/subscriptions');
      return response;
    } catch (error: any) {
      if (error.code) {
        throw new BillingApiError(
          error.message || 'Failed to cancel subscription',
          error.code,
          error.status || 400,
          error.details
        );
      }
      throw new BillingApiError(
        'Failed to cancel subscription',
        'CANCEL_SUBSCRIPTION_FAILED',
        400
      );
    }
  },

  /**
   * Check if user has active subscription
   */
  async hasActiveSubscription(): Promise<boolean> {
    try {
      const response = await httpClient.get<boolean>('/billing/subscriptions/status');
      return response;
    } catch (error: any) {
      // If endpoint fails, assume no active subscription
      return false;
    }
  },

  /**
   * Create a Stripe Customer Portal session
   */
  async createPortalSession(): Promise<{ portalUrl: string }> {
    try {
      const response = await httpClient.post<{ portalUrl: string }>('/billing/portal', {});
      return response;
    } catch (error: any) {
      if (error.code) {
        throw new BillingApiError(
          error.message || 'Failed to create portal session',
          error.code,
          error.status || 400,
          error.details
        );
      }
      throw new BillingApiError(
        'Failed to open billing portal. Please try again.',
        'PORTAL_SESSION_FAILED',
        400
      );
    }
  },

  /**
   * Redirect to Stripe checkout
   */
  redirectToCheckout(checkoutUrl: string): void {
    window.location.href = checkoutUrl;
  }
};
